import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams,
  ValueGetterParams
} from 'ag-grid-community';
import {
  dateFilterGetter,
  decodeGetter
} from '@/app/common/components/ag-grid/configs';
import timeStampToDate from '@/helpers/timeStampToDate';
import numberHumanizer from '@/helpers/numberHumanizer';
import MassiveStatusRenderer from '@/app/common/components/CellRenderers/MassiveStatusRenderer';

import { Link } from 'react-router-dom';
import { getColor } from '@/helpers/utils';
import { RemoveClusteringContext } from '@/context/Context';
import useDomain from '@/hooks/useDomain';
import { useContext } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { ClusteringHistoryItem } from '@/app/KeywordResearch/types/clusteringApiTypes';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

type ClusteringHistoryColumn = ColDef<ClusteringHistoryItem> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type RemoveClusteringContextValue = {
  setShow: (show: boolean) => void;
  setTaskId: (taskId: string) => void;
  setkeyword: (keyword: string) => void;
};

export const clusteringHistoryColumnConfig: ClusteringHistoryColumn[] = [
  {
    field: 'stem',
    type: 'string',
    title: 'نمونه کلمه',
    flex: 1,
    width: 250,
    minWidth: 150,
    tooltip:
      'اولین کلمه ای که شما وارد کردید به عنوان نمونه کلمه انتخاب میشه',
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    cellRenderer: (params: ICellRendererParams<ClusteringHistoryItem>) => {
      return (
        <Link
          to={`${params.data?.task_id}`}
          style={{
            color: getColor('navbar-vertical-default-link-active-color')
          }}
          className={`fw-bolder btn-link ${!params.data?.task_id && 'disabled-link'}`}
        >
          <div className="d-flex gap-1 align-items-baseline">
            {decodeURIComponent(String(params.value ?? ''))
              ?.replace("['", '')
              ?.replace("']", '')}
          </div>
        </Link>
      );
    },
    menuTabs,
    filterValueGetter: decodeGetter
  },
  {
    field: 'len_keywords',
    type: 'number',
    title: 'تعداد',
    width: 85,
    searchable: false,
    // filter: 'agTextColumnFilter',
    floatingFilter: false,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<ClusteringHistoryItem>) =>
      numberHumanizer(params.value) || params.value
  },
  {
    field: 'type_clustering',
    type: 'number',
    title: 'نوع',
    width: 85,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    cellRenderer: (params: ICellRendererParams<ClusteringHistoryItem>) => {
      if (params.value === 'kmean') return 'K-means';
      if (params.value === 'ai_clustering') return 'AI';
      return 'عادی';
    },
    filterValueGetter: (params: ValueGetterParams<ClusteringHistoryItem>) => {
      if (params.data?.type_clustering === 'kmean') return 'K-means';
      if (params.data?.type_clustering === 'ai_clustering') return 'AI';
      return 'عادی';
    },
    menuTabs
  },
  {
    field: 'status',
    type: 'number',
    title: 'وضعیت',
    width: 135,
    searchable: false,
    cellRenderer: (params: ICellRendererParams<ClusteringHistoryItem>) => (
      <MassiveStatusRenderer
        status={params.value}
        processingLabel="در حال خوشه‌بندی"
      />
    )
  },
  {
    field: 'created_at',
    type: 'string',
    title: 'تاریخ',
    width: 105,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    valueFormatter: params =>
      timeStampToDate(Number(params.value)).slice(8).toPersianDigit(),
    filterValueGetter: dateFilterGetter
  },
  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<ClusteringHistoryItem>) => {
      const { domain, domain_slug } = useDomain();
      const { setShow, setTaskId, setkeyword } = useContext(
        RemoveClusteringContext
      ) as RemoveClusteringContextValue;

      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                as={Link}
                to={`/keyword-research/clustering/${domain_slug}/${domain}/${params.data?.task_id}`}
                className={classNames(
                  'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1'
                )}
              >
                <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                مشاهده
              </Dropdown.Item>
              <Dropdown.Divider className="my-0" />
              <Dropdown.Item
                onClick={() => {
                  // if (params.data.status === 4)
                  //   warningMessage(
                  //     'در حال جستجو هستی. لطفا صبر کن تا جستجو تمام بشه یا ابتدا جستجو رو متوقف کن'
                  //   );
                  // else {
                  setTaskId(params.data?.task_id ?? '');
                  setkeyword(
                    decodeURIComponent(String(params.data?.stem ?? ''))
                      ?.replace("['", '')
                      ?.replace("']", '')
                  );
                  setShow(true);
                  // }
                }}
                className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                حذف
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </span>
      );
    }
  }
];
